import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import { useCallback, useState } from "react";
import { downloadCSV } from "../../../modules/Reports/utils/sheetjs/downloadCSV";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryPayrollSettings } from "../../../query/payments/queries";
import { PayeeRow } from "@wingspanhq/wingspan-api-sdk";
import { queriesPayeeRows } from "@query";
import { getPayables } from "../../../query/payments/selectors";
import { paymentsService } from "../../../services/payments";
import { getAllEntries } from "../../../utils/serviceHelper";
import { mapPayablesTableToCSV } from "../../screens/payables/csvUtils";
import { getMatchingPayroll } from "../../utils/payables";
import { mapPayableFilters, PayablesFilters } from "./PayablesTable";

export const useDownloadCsv = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { openSnackbar } = useWSSnackbar();
  const userId = useUserId();
  const payrollSettingsQuery = useQueryPayrollSettings(userId);

  const downloadCsv = useCallback(
    async (filters: PayablesFilters) => {
      try {
        setIsLoading(true);
        setError(null);

        const allPayeeRows: PayeeRow[] = [];
        let pageToken: string | undefined;
        do {
          const list = await queriesPayeeRows.searchPayeeRows.fetchQueryData({
            page: { size: 100, token: pageToken }
          });
          allPayeeRows.push(...(list.data ?? []));
          pageToken = list.pagination?.nextPageToken ?? undefined;
        } while (pageToken);

        const filter = mapPayableFilters(filters);

        const payables = getPayables(
          await getAllEntries(async (...args) => {
            return (await paymentsService.payable.list(...(args as any))).data;
          }, filter)
        );

        const csvData = mapPayablesTableToCSV(
          payables.map(payable => {
            const payeeRow = allPayeeRows.find(
              row => row.id === payable.memberId
            );

            const payrollRun = payrollSettingsQuery.data
              ? getMatchingPayroll(payrollSettingsQuery.data, payable)
              : undefined;

            return {
              payable,
              payeeRow,
              payrollRun
            };
          })
        );

        downloadCSV(csvData, "Invoices");

        openSnackbar({
          type: "success",
          message: "CSV downloaded"
        });
      } catch (error) {
        setError(error as any);

        console.error("Failed to download CSV", error);

        openSnackbar({
          type: "warning",
          message: "Failed to download CSV"
        });
      } finally {
        setIsLoading(false);
      }
    },
    [openSnackbar, payrollSettingsQuery.data]
  );

  return {
    downloadCsv,
    isLoading: payrollSettingsQuery.isLoading || isLoading,
    error
  };
};
